import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { FormBuilder, FormsModule, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { AsyncPipe, NgStyle, NgTemplateOutlet } from '@angular/common';
import { ChartRendererComponent } from '../visualization-renderer/chart-renderer.component';
import { ColorPickerModule } from 'ngx-color-picker';
import { LabelForValuePipe } from 'tsui/lib/pipes/label-for-option.pipe';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TsButtonModule, TsColorBubbleComponent, TsDividerComponent, TsInputComponent } from 'tsui';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { TsEmptyStateComponent } from 'tsui/lib/ts-empty-state/ts-empty-state.component';
import { TsPanelComponent } from 'tsui/lib/ts-panel/ts-panel.component';
import { TsSelectModule } from 'tsui/lib/ts-select/ts.select.module';
import { PanelFormFields } from '../../models/panel-form-fields.enum';
import { AccountState } from '@state/account/state';
import { FinancialStatementTypes, ReportPeriod, PanelFormBase, FinancialStatementEntry } from '../../models';
import { AssetManagementService } from '../../services/asset-management.service';
import { ReportPeriodUtils } from '../../utils/report-period.utils';
import { combineLatestWith, filter, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, startWith } from 'rxjs';
import { FinancialStatementGridComponent } from '../financial-statement-grid/financial-statement-grid.component';

export interface FinancialReportPanelBuilderDialogData extends PanelFormBase {
  type: FinancialStatementTypes;
  reportPeriod: ReportPeriod;
  propertyId: number;
}

enum FinancialReportFormFields {
  reportPeriod = 'reportPeriod',
}

@Component({
  selector: 'financial-report-panel-builder-dialog',
  standalone: true,
  imports: [
    AsyncPipe,
    ChartRendererComponent,
    ColorPickerModule,
    FormsModule,
    LabelForValuePipe,
    MatButtonToggleModule,
    MatOptionModule,
    MatSelectModule,
    MatSlideToggleModule,
    NgTemplateOutlet,
    ReactiveFormsModule,
    TsButtonModule,
    TsColorBubbleComponent,
    TsDialogComponent,
    TsDividerComponent,
    TsEmptyStateComponent,
    TsInputComponent,
    TsPanelComponent,
    TsSelectModule,
    FinancialStatementGridComponent,
    NgStyle,
  ],
  templateUrl: './financial-report-panel-builder-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FinancialReportPanelBuilderDialogComponent implements OnInit {
  readonly data: FinancialReportPanelBuilderDialogData = inject(DIALOG_DATA);
  readonly dialogRef = inject(DialogRef);
  readonly fb = inject(FormBuilder);
  readonly store = inject(Store);
  readonly assetManagementService = inject(AssetManagementService);

  readonly accountColorOptions$ = this.store.select(AccountState.selectAccountColorsWithNone);
  readonly reportPeriodOptions = ReportPeriodUtils.buildReportPeriodOptions(['custom']);
  readonly reportPeriodValues = ReportPeriodUtils.reportPeriodValues;
  readonly previewLoading$ = new BehaviorSubject<boolean>(false);
  readonly financialReportOptions = [
    { label: 'Balance Sheet', value: 'balance-sheet' as FinancialStatementTypes },
    { label: 'Income Statement', value: 'income-statement' as FinancialStatementTypes },
  ];

  readonly panelForm: UntypedFormGroup = this.fb.group({
    [PanelFormFields.title]: [this.editFinancialReportData?.title || '', Validators.required],
    [PanelFormFields.headerColor]: [this.editFinancialReportData?.headerColor || null],
    [PanelFormFields.type]: [this.editFinancialReportData?.type || '', Validators.required],
    [FinancialReportFormFields.reportPeriod]: [
      this.editFinancialReportData?.reportPeriod?.key || null,
      Validators.required,
    ],
  });

  financialStatementPreview$: Observable<FinancialStatementEntry[]>;

  get panelFormControls(): { [key in PanelFormFields | FinancialReportFormFields]: any } {
    return {
      [PanelFormFields.title]: this.panelForm.get(PanelFormFields.title),
      [PanelFormFields.headerColor]: this.panelForm.get(PanelFormFields.headerColor),
      [PanelFormFields.type]: this.panelForm.get(PanelFormFields.type),
      [FinancialReportFormFields.reportPeriod]: this.panelForm.get(FinancialReportFormFields.reportPeriod),
    };
  }

  get editFinancialReportData(): FinancialReportPanelBuilderDialogData {
    return this.data;
  }

  ngOnInit() {
    const typeControlListener = this.panelFormControls[PanelFormFields.type].valueChanges.pipe(
      startWith(this.data?.type),
    );
    const reportPeriodControlListener = this.panelFormControls[
      FinancialReportFormFields.reportPeriod
    ].valueChanges.pipe(startWith(this.data?.reportPeriod?.key));

    this.financialStatementPreview$ = typeControlListener.pipe(
      combineLatestWith(reportPeriodControlListener),
      filter(([type, reportPeriod]) => !!type && !!reportPeriod),
      tap(() => this.previewLoading$.next(true)),
      switchMap(([type, reportPeriod]) => {
        return this.assetManagementService
          .getFinancialStatement(this.data.propertyId, type, ReportPeriodUtils.reportPeriodValues[reportPeriod])
          .pipe(tap(() => this.previewLoading$.next(false)));
      }),
    );
  }

  createPanel() {
    const reportPeriod = this.reportPeriodValues[this.panelFormControls[FinancialReportFormFields.reportPeriod].value];

    if (!reportPeriod) {
      console.error('Invalid report period');
      return;
    }

    this.dialogRef.close(<FinancialReportPanelBuilderDialogData>{
      ...this.panelForm.value,
      reportPeriod,
    });
  }
}
